import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import './Payments.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getCurrencySymbol = (code) => {
  const symbols = { USD: '$', EUR: '€', GBP: '£', AED: 'د.إ', SGD: 'S$', NGN: '₦' };
  return symbols[code] || '$';
};

const Payments = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const socket = useSocket();

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedContract, setSelectedContract] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const fetchContracts = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/contracts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setContracts(data.contracts || []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  useEffect(() => {
    if (!socket) return;
    const refresh = () => fetchContracts();
    socket.on('contract_status_change', refresh);
    socket.on('payment_uploaded', refresh);
    socket.on('payment_verified', refresh);
    socket.on('payment_released', refresh);
    return () => {
      socket.off('contract_status_change', refresh);
      socket.off('payment_uploaded', refresh);
      socket.off('payment_verified', refresh);
      socket.off('payment_released', refresh);
    };
  }, [socket, fetchContracts]);

  const getStatusClass = (status) => {
    switch (status) {
      case 'pending_payment': return 'pending';
      case 'payment_uploaded': return 'uploaded';
      case 'payment_verified': return 'verified';
      case 'work_in_progress': return 'progress';
      case 'work_completed': return 'completed';
      case 'payment_released': return 'released';
      default: return 'pending';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending_payment': return 'Pending Payment';
      case 'payment_uploaded': return 'Awaiting Verification';
      case 'payment_verified': return 'Payment Verified';
      case 'work_in_progress': return 'Work In Progress';
      case 'work_completed': return 'Work Completed';
      case 'payment_released': return 'Payment Released';
      default: return status;
    }
  };

  const filteredContracts = contracts.filter(c => {
    const matchSearch = c.vesselName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        c.serviceType?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === 'all' || c.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const stats = {
    total: contracts.length,
    pending: contracts.filter(c => c.status === 'pending_payment').length,
    inEscrow: contracts.filter(c => c.status === 'payment_uploaded' || c.status === 'payment_verified').length,
    released: contracts.filter(c => c.status === 'payment_released').length
  };

  if (loading) return <div className="payments-loader"><i className="fas fa-spinner fa-spin"></i></div>;

  return (
    <div className="payments-wrapper">
      <div className="payments-header">
        <div className="payments-header-text">
          <h1><i className="fas fa-hand-holding-usd"></i> Payments</h1>
          <p>Track contract payments and upload proof</p>
        </div>
      </div>

      <div className="payments-stats">
        <div className="stat-box">
          <div className="stat-number">{stats.total}</div>
          <div className="stat-label">Total Contracts</div>
        </div>
        <div className="stat-box">
          <div className="stat-number">{stats.pending}</div>
          <div className="stat-label">Pending Payment</div>
        </div>
        <div className="stat-box">
          <div className="stat-number">{stats.inEscrow}</div>
          <div className="stat-label">In Escrow</div>
        </div>
        <div className="stat-box">
          <div className="stat-number">{stats.released}</div>
          <div className="stat-label">Released</div>
        </div>
      </div>

      <div className="payments-controls">
        <div className="payments-search">
          <i className="fas fa-search"></i>
          <input type="text" placeholder="Search vessel or service..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="payments-filter">
          <button className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`} onClick={() => setFilterStatus('all')}>All</button>
          <button className={`filter-btn ${filterStatus === 'pending_payment' ? 'active' : ''}`} onClick={() => setFilterStatus('pending_payment')}>Pending Payment</button>
          <button className={`filter-btn ${filterStatus === 'payment_uploaded' ? 'active' : ''}`} onClick={() => setFilterStatus('payment_uploaded')}>Under Review</button>
          <button className={`filter-btn ${filterStatus === 'payment_released' ? 'active' : ''}`} onClick={() => setFilterStatus('payment_released')}>Released</button>
        </div>
      </div>

      <div className="payments-table-container">
        <table className="payments-table">
          <thead>
            <tr>
              <th>Vessel</th>
              <th>Service</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredContracts.length === 0 ? (
              <tr><td colSpan="5" className="empty-row">No contracts found.</td></tr>
            ) : (
              filteredContracts.map(c => {
                const currency = c.currency || 'USD';
                const currencySymbol = getCurrencySymbol(currency);
                return (
                  <tr key={c._id}>
                    <td>{c.vesselName}</td>
                    <td>{c.serviceType}</td>
                    <td className="payment-amount">{currencySymbol}{c.totalAmount?.toLocaleString()} {currency}</td>
                    <td><span className={`payment-status ${getStatusClass(c.status)}`}>{getStatusLabel(c.status)}</span></td>
                    <td className="actions-cell">
                      <button className="action-icon" onClick={() => { setSelectedContract(c); setShowDetailsModal(true); }} title="View Details"><i className="fas fa-eye"></i></button>
                      {c.status === 'pending_payment' && (
                        <button className="action-icon deposit" onClick={() => navigate(`/owner/contract/${c._id}`)} title="Make Payment"><i className="fas fa-credit-card"></i></button>
                      )}
                      {c.status === 'work_completed' && (
                        <button className="action-icon release" onClick={() => navigate(`/owner/contract/${c._id}`)} title="Release Payment"><i className="fas fa-hand-holding-usd"></i></button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showDetailsModal && selectedContract && (
        <div className="payments-modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="payments-modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2><i className="fas fa-receipt"></i> Contract Details</h2><button className="modal-close" onClick={() => setShowDetailsModal(false)}>&times;</button></div>
            <div className="modal-body">
              <div className="details-grid">
                <div><span className="label">Vessel</span><span>{selectedContract.vesselName}</span></div>
                <div><span className="label">Service</span><span>{selectedContract.serviceType}</span></div>
                <div><span className="label">Provider</span><span>{selectedContract.vendorName || 'N/A'}</span></div>
                <div>
                  <span className="label">Total Amount</span>
                  <span className="amount-highlight">
                    {getCurrencySymbol(selectedContract.currency || 'USD')}{selectedContract.totalAmount?.toLocaleString()} {selectedContract.currency || 'USD'}
                  </span>
                </div>
                <div><span className="label">Status</span><span className={`status-badge ${selectedContract.status}`}>{selectedContract.status}</span></div>
                <div><span className="label">Created</span><span>{new Date(selectedContract.createdAt).toLocaleDateString()}</span></div>
              </div>
              {selectedContract.paymentProof?.imageUrl && (
                <div className="proof-preview"><span className="label">Payment Proof</span><img src={selectedContract.paymentProof.imageUrl} alt="Proof" /></div>
              )}
              {selectedContract.bankDetails && (
                <div className="bank-details">
                  <span className="label">Bank Account</span>
                  <p><strong>Account:</strong> {selectedContract.bankDetails.accountName}</p>
                  <p><strong>Bank:</strong> {selectedContract.bankDetails.bankName}</p>
                  <p><strong>IBAN:</strong> {selectedContract.bankDetails.iban}</p>
                  <p><strong>Swift:</strong> {selectedContract.bankDetails.swiftCode}</p>
                </div>
              )}
            </div>
            <div className="modal-footer"><button className="modal-close-btn" onClick={() => setShowDetailsModal(false)}>Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;